import { Box, Group, SimpleGrid } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentAdoptionSummary } from '../../../api/agent-miner-client/agent-miner-client.type';
import { SingleValueCard } from '../../../components/overview-card/single-value-card';
import { inkLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { fmtMetric } from '../model-comparison/model-comparison.helpers';

type Props = {
  summary: AgentAdoptionSummary | undefined;
  isLoading: boolean;
};

/**
 * Tile title with an info icon whose tooltip clarifies the metric's
 * definition — the adoption rate's denominator in particular needs spelling
 * out.
 *
 * @param {string} label - The visible tile title
 * @param {string} tooltip - The explanation shown on hover
 * @returns {ReactNode} The composed title content
 */
const titleWithInfo = (label: string, tooltip: string): ReactNode => (
  <Group gap={4} wrap="nowrap">
    {label}
    <Tooltip label={tooltip} position="top" variant="white" events={{ hover: true, focus: true, touch: false }}>
      <Box component="span" tabIndex={0} aria-label={tooltip} style={{ display: 'inline-flex', cursor: 'help' }}>
        <Icon name="info" size={13} color={inkLight} />
      </Box>
    </Tooltip>
  </Group>
);

/**
 * KPI strip for the Adoption tab: active users, adoption rate, sessions, and
 * median turns per session. Every nullable value renders as an em-dash rather
 * than NaN. (The team-resolved attribution share lives in the Attribution
 * view, not up here.)
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} A four-tile KPI row
 */
export const AdoptionKpiStrip = ({ summary, isLoading }: Props) => {
  const { t } = useTranslation();

  return (
    <SimpleGrid cols={{ base: 2, lg: 4 }} spacing={16} data-testid="adoption-kpi-strip">
      <SingleValueCard
        title={titleWithInfo(t('agent_miner.adoption.active_users'), t('agent_miner.adoption.active_users_tooltip'))}
        icon="person"
        value={summary ? summary.active_agent_users.toLocaleString() : '—'}
        unit={summary ? t('agent_miner.adoption.active_users_unit', { eligible: summary.eligible_users }) : ''}
        isLoading={isLoading}
      />
      <SingleValueCard
        title={titleWithInfo(t('agent_miner.adoption.adoption_rate'), t('agent_miner.adoption.adoption_rate_tooltip'))}
        icon="groups"
        value={fmtMetric(summary?.adoption_rate)}
        unit="%"
        isLoading={isLoading}
      />
      <SingleValueCard
        title={t('agent_miner.adoption.sessions_kpi')}
        icon="terminal"
        value={summary ? summary.total_sessions.toLocaleString() : '—'}
        isLoading={isLoading}
      />
      <SingleValueCard
        title={titleWithInfo(t('agent_miner.adoption.median_turns'), t('agent_miner.adoption.median_turns_tooltip'))}
        icon="forum"
        value={fmtMetric(summary?.median_turns_per_session)}
        isLoading={isLoading}
      />
    </SimpleGrid>
  );
};
